import React, { memo } from 'react';
import areNodeRendererPropsEqual from './areNodeRendererPropsEqual';
import useNodeRenderer from './hooks/useNodeRenderer';
import buildCommonProps from './utils/buildCommonProps';
import deduplicateTasks from './utils/deduplicateTasks';
import MilestoneNodeItem from './components/MilestoneNodeItem';
import NoteNodeItem from './components/NoteNodeItem';
import DecisionNodeItem from './components/DecisionNodeItem';
import TaskNodeItem from './components/TaskNodeItem';
import FlowNodeShell from './components/FlowNodeShell';
import useCardSlots from './hooks/useCardSlots';
import { NODE_WIDTH, NODE_HEIGHT } from '../../hooks/utils/canvasUtils';

/**
 * NodeRenderer Component
 *
 * Renders all task nodes (regular, decision, and milestone) in the flowchart.
 * Handles node type switching and passes appropriate props to each node type.
 */
const NodeRenderer = memo(function NodeRendererInner(props) {
  const {
    tasks,
    fullNodeIds = null,
    statuses,
    positionsToUse,
    nodeDimensions,
    selectedNodes,
    draggingNode,
    connectingFrom,
    connectingTarget,
    connectingBranch,
    isConnecting,
    focusedTaskIds,
    isAnimatingLayout,
    isFocusMode,
    lockStates,
    allCategories,
    allTags,
    allTasks,
    nestedSprints,
    allSprints = [],
    containerRef,
    pan,
    zoom,
    dropTarget,
    layoutSurfaceEpoch = 0,
    getNodeAnimationState,
    handleNodeDragStart,
    handleNodeClick,
    handleConnectionDragStart,
    handleFocusTask,
    handleDimensionsChange,
    onRenameTask,
    onDecisionAnswerChange,
    onDecisionOptionsChange,
    onDeleteTask,
    onOpenNestedSprint,
    onCreateNestedSprint,
    onRemoveNestedSprint,
    onDeliverablesChange,
    onInputsChange,
    onDependencyChange,
    onMilestoneAnchorChange,
    onSetStatusAutoSync,
    onCopy,
    onDuplicate,
    cardDisplaySettings = {},
    onQuickViewMouseEnter,
    onQuickViewMouseLeave,
    onQuickViewMouseMove,
    onExplodeNestedCampaign,
    hoveredMissionId = null,
    onMissionHover,
    onMissionLeave,
  } = props;

  const { isQuickViewEnabled, nestedSprintMap, createNodeHandlers } = useNodeRenderer({
    pan,
    zoom,
    nestedSprints,
    containerRef,
    handleNodeDragStart,
    handleNodeClick,
    handleConnectionDragStart,
    onDeleteTask,
    onCopy,
    onDuplicate,
    handleFocusTask,
    onRemoveNestedSprint,
    onQuickViewMouseEnter,
    onQuickViewMouseLeave,
    onQuickViewMouseMove,
  });

  // Debug: Log positions for campaigns in multi-campaign mode
  if (tasks.length > 0 && tasks[0].sourceCampaign && !window._nodeRendererLogged) {
    window._nodeRendererLogged = true;
    const byCampaign = {};
    tasks.forEach(t => {
      const name = t.sourceCampaign?.name || 'unknown';
      if (!byCampaign[name]) byCampaign[name] = [];
      byCampaign[name].push(positionsToUse[t.id]?.y);
    });
    Object.entries(byCampaign).forEach(([name, yValues]) => {
      const uniqueYs = [...new Set(yValues.filter(y => y !== undefined))];
      console.log(`[NodeRenderer] "${name}": ${yValues.length} tasks, ${uniqueYs.length} unique Y`, uniqueYs.sort((a, b) => a - b));
    });
  }

  const uniqueTasks = deduplicateTasks(tasks);
  const fullTasks = [];
  const shellTasks = [];
  uniqueTasks.forEach((task) => {
    if (fullNodeIds && !fullNodeIds.has(task.id)) shellTasks.push(task);
    else fullTasks.push(task);
  });
  const cardSlots = useCardSlots(fullTasks);

  const renderFullCard = (task, slotId) => {
    const position = positionsToUse[task.id] || { x: 0, y: 0 };
    const { isFadingOut, isFadingIn, isSliding } = getNodeAnimationState(task.id);
    const handlers = createNodeHandlers(task.id);

    const commonProps = buildCommonProps({
      task,
      position,
      layoutSurfaceEpoch,
      selectedNodes,
      draggingNode,
      connectingFrom,
      isConnecting,
      connectingTarget,
      focusedTaskIds,
      isFocusMode,
      isAnimatingLayout,
      isSliding,
      isFadingOut,
      isFadingIn,
      dropTarget,
      hoveredMissionId,
      zoom,
      handlers,
      isQuickViewEnabled,
      onMissionHover,
      onMissionLeave,
      onRenameTask,
      handleDimensionsChange,
    });

    if (task.nodeType === 'note') {
      return (
        <NoteNodeItem
          key={slotId}
          task={task}
          commonProps={commonProps}
          handlers={handlers}
        />
      );
    }

    if (task.nodeType === 'milestone') {
      return (
        <MilestoneNodeItem
          key={slotId}
          task={task}
          commonProps={commonProps}
          handlers={handlers}
          cardDisplaySettings={cardDisplaySettings}
          allTasks={allTasks}
          nestedSprints={nestedSprints}
          allSprints={allSprints}
          statuses={statuses}
          onMilestoneAnchorChange={onMilestoneAnchorChange}
          positionsToUse={positionsToUse}
        />
      );
    }

    if (task.nodeType === 'decision') {
      return (
        <DecisionNodeItem
          key={slotId}
          task={task}
          commonProps={commonProps}
          handlers={handlers}
          props={props}
          connectingFrom={connectingFrom}
          connectingBranch={connectingBranch}
          lockStates={lockStates}
          statuses={statuses}
          allCategories={allCategories}
          allTags={allTags}
          allTasks={allTasks}
          onDecisionAnswerChange={onDecisionAnswerChange}
          onDecisionOptionsChange={onDecisionOptionsChange}
        />
      );
    }

    return (
      <TaskNodeItem
        key={slotId}
        task={task}
        commonProps={commonProps}
        handlers={handlers}
        props={props}
        lockStates={lockStates}
        statuses={statuses}
        allCategories={allCategories}
        allTags={allTags}
        allTasks={allTasks}
        nestedSprintMap={nestedSprintMap}
        nestedSprints={nestedSprints}
        allSprints={allSprints}
        onOpenNestedSprint={onOpenNestedSprint}
        onCreateNestedSprint={onCreateNestedSprint}
        onDeliverablesChange={onDeliverablesChange}
        onInputsChange={onInputsChange}
        onDependencyChange={onDependencyChange}
        onSetStatusAutoSync={onSetStatusAutoSync}
        onExplodeNestedCampaign={onExplodeNestedCampaign}
      />
    );
  };

  return [
    ...shellTasks.map((task) => {
      const position = positionsToUse[task.id] || { x: 0, y: 0 };
      const dims = nodeDimensions?.[task.id] || { width: NODE_WIDTH, height: NODE_HEIGHT };
      return (
        <FlowNodeShell
          key={`shell-${task.id}`}
          task={task}
          position={position}
          width={dims.width}
          height={dims.height}
        />
      );
    }),
    ...cardSlots.map(({ slotId, task }) => renderFullCard(task, slotId)),
  ];
}, areNodeRendererPropsEqual);

NodeRenderer.displayName = 'NodeRenderer';

export default NodeRenderer;
